import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

function buildSchema(t: (key: string) => string) {
  return z.object({
    code: z.string().min(1, t('form.validation.codeRequired')),
    name: z.string().min(1, t('form.validation.nameRequired')),
    description: z.string().nullable(),
  });
}

export type ZoneFormValues = z.infer<ReturnType<typeof buildSchema>>;

interface ZoneFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: ZoneFormValues) => void;
  loading?: boolean;
}

export default function ZoneFormDialog({ open, onClose, onSubmit, loading }: ZoneFormDialogProps) {
  const { t } = useTranslation('warehouseMaster');
  const schema = buildSchema(t);

  const { control, handleSubmit, reset } = useForm<ZoneFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', name: '', description: null },
  });

  useEffect(() => {
    if (open) reset({ code: '', name: '', description: null });
  }, [open, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('addZone')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Controller
            name="code"
            control={control}
            render={({ field, fieldState }) => (
              <TextField {...field} label={t('form.code')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
            )}
          />
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState }) => (
              <TextField {...field} label={t('form.name')} fullWidth error={Boolean(fieldState.error)} helperText={fieldState.error?.message} />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => <TextField {...field} value={field.value ?? ''} label={t('form.description')} fullWidth multiline rows={2} />}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
